<template>
  <v-snackbar
    v-if="notification"
    v-model="snackbar"
    location="top"
    style="margin-top: calc(56px + var(--lew-deployment-banner-height, 0px))"
    color="error"
    :timeout="timeout"
    :close-on-back="false"
    @update:model-value="onDismiss"
    @click="snackbar = false"
  >
    {{ notification.message }}

    <!-- Only for failures that reached Sentry (services/api.js decides). Meant to be copied into a
         support mail, hence the wider timeout; @click.stop keeps the copy from dismissing. -->
    <div v-if="notification.traceId" class="trace-ref">
      <span class="trace-ref__label">{{ $t("general.error.reference") }}</span>
      <code class="trace-ref__id">{{ notification.traceId }}</code>
      <v-btn
        variant="text"
        size="x-small"
        density="comfortable"
        :icon="copied ? 'mdi-check' : 'mdi-content-copy'"
        :aria-label="copied ? $t('general.error.referenceCopied') : $t('general.error.referenceCopy')"
        @click.stop="copyReference"
      />
    </div>
  </v-snackbar>
</template>

<script>
import { computed, ref, watch } from "vue"
import { useNotificationStore } from "@/store/notification"

// Long enough to copy a 32-character id; a plain message has nothing to act on.
const TIMEOUT_WITH_REFERENCE = 15000
const TIMEOUT_DEFAULT = 6000

export default {
  name: "LewErrorNotification",
  setup() {
    const store = useNotificationStore()
    const snackbar = ref(false)
    const copied = ref(false)
    const notification = computed(() => store.getError)
    const timeout = computed(() => (notification.value?.traceId ? TIMEOUT_WITH_REFERENCE : TIMEOUT_DEFAULT))

    watch(
      () => notification.value,
      newValue => {
        if (newValue && newValue.message) {
          snackbar.value = true
          copied.value = false
        }
      }
    )

    const onDismiss = value => {
      if (!value) {
        store.dismissError()
      }
    }

    // Refused on an insecure origin; the id stays on screen, so a failure needs no message.
    const copyReference = async () => {
      try {
        await navigator.clipboard.writeText(notification.value.traceId)
        copied.value = true
      } catch {
        copied.value = false
      }
    }

    return { snackbar, notification, timeout, copied, onDismiss, copyReference }
  },
}
</script>

<style scoped>
.trace-ref {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  opacity: 0.9;
}

.trace-ref__id {
  font-family: monospace;
  /* Longer than a phone screen is wide, and truncating it would defeat the point. */
  word-break: break-all;
}
</style>
